import type { ParentComponent } from "solid-js";
import { onCleanup, onMount } from "solid-js";
import { isNil } from "lodash-es";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { extractClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { getReorderDestinationIndex } from "@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index";
import { autoScrollForElements } from "@atlaskit/pragmatic-drag-and-drop-auto-scroll/element";
import type { DraggableData, DraggableType } from "./util";
import { isDraggableDataOfType } from "./util";

export type DraggableListProps = {
  scrollContainer: HTMLDivElement;
  monitorDraggableType: DraggableType;
  items: { id: string }[];
  onDragStart?: (sourceData: DraggableData) => void;
  onDrop: (sourceData: DraggableData, index: number) => void;
};

const DraggableList: ParentComponent<DraggableListProps> = (props) => {
  onMount(() => {
    onCleanup(
      combine(
        monitorForElements({
          canMonitor: ({ source }) => isDraggableDataOfType(source.data, props.monitorDraggableType),
          onDragStart: ({ source }) => {
            const sourceData = source.data;
            if (!isDraggableDataOfType(sourceData, props.monitorDraggableType)) {
              return;
            }

            props.onDragStart?.(sourceData); // TODO, test
          },
          onDrop: ({ source, location }) => {
            const target = location.current.dropTargets[0];
            if (isNil(target)) {
              return;
            }

            const sourceData = source.data;
            const targetData = target.data;
            if (
              !isDraggableDataOfType(sourceData, props.monitorDraggableType) ||
              !isDraggableDataOfType(targetData, props.monitorDraggableType)
            ) {
              return;
            }

            const indexOfTarget = props.items.findIndex((item) => item.id === targetData.id);
            if (isNil(indexOfTarget)) {
              return;
            }

            const closestEdgeOfTarget = extractClosestEdge(targetData);
            const index = getReorderDestinationIndex({
              startIndex: sourceData.index,
              closestEdgeOfTarget,
              indexOfTarget,
              axis: "vertical",
            });

            props.onDrop(sourceData, index);
          },
        }),
        autoScrollForElements({
          element: props.scrollContainer,
        }),
      ),
    );
  });

  return <>{props.children}</>;
};

export default DraggableList;
